import React from 'react';
import { createRoot } from 'react-dom/client';
import { STAY_LOGGED_IN_STORAGE_ID } from 'constants/settings';
import { isRecoverableLoadError, handleChunkLoadError } from 'utils/chunkErrorRecovery';
import { initSentryEarly } from './setupIntegrations';
import { store, persistor } from './store';
import App from './app';
import httpService from './api/interceptors';
// Add our custom commutifi icon fonts and text fonts
import '@commutifi-fe/fonts/fonts.css';
import './styles/main.scss';
import 'mapbox-gl/dist/mapbox-gl.css';

// ============================================================================
// Sentry Early Initialization - MUST happen before React renders
// ============================================================================
// Initialize Sentry with GDPR-compliant masked mode BEFORE the React app loads.
// This ensures ALL errors (including those during initial render) have Session
// Replay attached. Without this, errors that occur before CookieConsent mounts
// would have no replay data.
//
// Privacy: Masked mode captures no PII and is considered "strictly necessary"
// under GDPR. Full replay (unmasked) requires Analytics consent and will be
// enabled on page reload after user grants consent via CookieConsent.
// ============================================================================
initSentryEarly();
async function enableMocking() {
  if (process.env.ENABLE_API_MOCK !== 'true') {
    return;
  }
  try {
    const {
      worker
    } = await import('api/__mocks__/browser');

    // `worker.start()` returns a Promise that resolves
    // once the Service Worker is up and ready to intercept requests.
    return await worker.start({
      onUnhandledRequest: 'bypass'
    });
  } catch {
    // eslint-disable-next-line no-console
    console.warn('MSW not available in production environment');
    return Promise.resolve();
  }
}

// ============================================================================
// ChunkLoadError Recovery - Global handler for async chunk loading failures
// ============================================================================
// React's ErrorBoundary in app.tsx catches ChunkLoadErrors during rendering,
// but it CANNOT catch errors from async dynamic imports (promise rejections).
//
// This handler catches unhandled promise rejections for ChunkLoadErrors
// (both JS and CSS chunks) and auto-reloads to recover.
//
// See utils/chunkErrorRecovery.ts for centralized constants and utilities.
// ============================================================================
window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
  const error = event.reason as Error | null;
  if (isRecoverableLoadError(error)) {
    // eslint-disable-next-line no-console
    console.error('ChunkLoadError caught by global handler:', error?.message);
    handleChunkLoadError();
  }
});
const mountNode = document.getElementById('root');
const root = createRoot(mountNode || document.createElement('div'));

// ========================================================
// Accessibility analysis
// Track React 18 support: https://github.com/dequelabs/axe-core-npm/issues/500
// ========================================================
if (process.env.NODE_ENV !== 'production') {
  // eslint-disable-next-line @typescript-eslint/no-require-imports -- All good
  const axe = require('@axe-core/react');
  axe(React, root, 1000);
}

/**
 * Transfers sessionStorage from one tab to another. Those events are possible only from the same domain!
 * Source for solution: https://blog.guya.net/2015/06/12/sharing-sessionstorage-between-tabs-for-secure-multi-tab-authentication/
 * Source for event handling: https://stackoverflow.com/questions/20325763/browser-sessionstorage-share-between-tabs
 */
const sessionStorageTransfer = (_event: StorageEvent) => {
  const event = _event || window.event as StorageEvent;
  if (!event) return;
  if (!event.newValue || !event.isTrusted) return;
  if (event.key === 'getSessionStorage') {
    // another tab asked for the sessionStorage -> send it
    localStorage.setItem('sessionStorage', JSON.stringify(sessionStorage));
    // the other tab should now have it, so we're done with it.
    localStorage.removeItem('sessionStorage'); // <- could do short timeout as well.
  } else if (event.key === 'sessionStorage' && !sessionStorage.length) {
    // another tab sent data <- get it
    const data = JSON.parse(event.newValue);
    Object.keys(data).forEach(key => {
      sessionStorage.setItem(key, data[key]);
    });
  }
};

// listen for changes to localStorage
if (window.addEventListener) {
  window.addEventListener('storage', sessionStorageTransfer, false);
}

// Ask other tabs for session storage (this is ONLY to trigger event). This use case is only
// to allow opening new tabs and not logging out the user when the option to stay logged in is not selected
if (!Object.keys(sessionStorage).filter(key => !key.startsWith('persist:')).length && localStorage.getItem(STAY_LOGGED_IN_STORAGE_ID) === 'false') {
  localStorage.setItem('getSessionStorage', 'please');
  localStorage.removeItem('getSessionStorage');
}

// ============================================================================
// App Initialization
// ============================================================================
// Both setupInterceptors and enableMocking are async:
// - setupInterceptors: Dynamically imports fetch-intercept to avoid test environment issues
// - enableMocking: Starts MSW service worker in development with ENABLE_API_MOCK=true
//
// We await both before rendering to ensure all interceptors are registered
// before any API calls are made, preventing race conditions.
// ============================================================================
async function initializeApp() {
  await httpService.setupInterceptors(store);
  await enableMocking();
  root.render(<App store={store} persistor={persistor} />);
}
initializeApp().catch(error => {
  // eslint-disable-next-line no-console
  console.error('Failed to initialize app', error);
});